import { useCallback, useEffect, useRef, useState } from "preact/hooks"
import type { Me } from "../types"
import { About } from "./About"
import { FriendsView } from "./FriendsView"
import { Home } from "./Home"
import { KidHome } from "./KidHome"
import { KidNavbar } from "./KidNavbar"
import { Landing } from "./Landing"
import { Legal } from "./Legal"
import { Login } from "./Login"
import { fetchMe } from "./lib/api"
import { MatchPlay } from "./MatchPlay"
import { Navbar } from "./Navbar"
import { ForgotPassword, ResetPassword } from "./PasswordReset"
import { Profile } from "./Profile"
import { SoloPlay } from "./SoloPlay"
import { VerifyEmail, VerifyNotice } from "./VerifyEmail"

function parseSoloRoute(path: string): number | null {
	if (!path.startsWith("/solo/")) return null
	const id = Number(path.slice("/solo/".length))
	return Number.isInteger(id) && id > 0 ? id : null
}

function parseMatchRoute(path: string): number | null {
	if (!path.startsWith("/match/")) return null
	const id = Number(path.slice("/match/".length))
	return Number.isInteger(id) && id > 0 ? id : null
}

// parseUserRoute extracts the username from /u/:username public profiles.
function parseUserRoute(path: string): string | null {
	if (!path.startsWith("/u/")) return null
	const username = path.slice("/u/".length)
	return username.length > 0 ? username : null
}

// The signed-in routes a kid profile may not reach. Everything else — "/" and
// any path the router does not recognise — lands on KidHome, exactly the way
// an unknown path lands on Home for the owner. The server enforces the same
// boundary (isProfileAllowedPath); this only keeps the app from offering a
// page whose every request would 403.
function kidBlockedPath(path: string): boolean {
	return (
		path === "/friends" ||
		path === "/me" ||
		path === "/about" ||
		path === "/privacy" ||
		path === "/terms" ||
		parseUserRoute(path) !== null ||
		parseMatchRoute(path) !== null
	)
}

export function App() {
	const [path, setPath] = useState(window.location.pathname)
	// "loading" until the initial GET /api/auth/me resolves.
	const [user, setUser] = useState<Me | null | "loading">("loading")
	// Set when the session check itself failed. This is NOT the same as being
	// signed out: fetchMe resolves null for a 401 (genuinely logged out) and
	// rejects for everything else — a 500, a proxy blip, an offline phone. Any
	// of those left as `user = null` would sign a perfectly valid session out
	// and bounce the player to /login, where they'd log in to the session they
	// already had. So a rejection keeps the session and says so instead.
	const [sessionError, setSessionError] = useState("")

	const navigate = useCallback((to: string) => {
		window.history.pushState({}, "", to)
		// Route on the pathname only — query/hash live in the URL but must not
		// leak into route matching, which compares bare pathnames.
		setPath(new URL(to, window.location.href).pathname)
	}, [])

	// Keep a ref so the global click handler always calls the latest navigate.
	const navigateRef = useRef(navigate)
	navigateRef.current = navigate

	// loadSession re-runs the session check. On failure it parks `user` back at
	// "loading" so the shell renders the error screen below (with a retry)
	// rather than a spinner that never stops, and never clears a live session.
	const loadSession = useCallback(() => {
		setSessionError("")
		fetchMe()
			.then(setUser)
			.catch((e: unknown) => {
				setUser("loading")
				setSessionError(
					e instanceof Error ? e.message : "Couldn't check your session.",
				)
			})
	}, [])

	useEffect(() => {
		loadSession()
	}, [loadSession])

	// After POST /api/me/profiles/switch the session is a different player:
	// re-read /api/auth/me (which is what flips the whole shell between the
	// owner's app and kid mode) and start again from "/", since the page that
	// asked for the switch is often not one the new player may see.
	const onProfileChanged = useCallback(() => {
		loadSession()
		navigate("/")
	}, [loadSession, navigate])

	// Global SPA link handler — intercepts same-origin <a> clicks so they
	// don't cause a full page reload.
	useEffect(() => {
		const handler = (event: MouseEvent) => {
			const a = (event.target as Element).closest("a")
			if (!a) return

			if (
				a.target ||
				a.hasAttribute("download") ||
				event.button !== 0 ||
				event.ctrlKey ||
				event.metaKey ||
				event.shiftKey ||
				event.altKey
			) {
				return
			}

			let url: URL
			try {
				url = new URL(a.href, window.location.href)
			} catch {
				return
			}
			if (url.origin !== window.location.origin) return
			if (url.protocol !== "http:" && url.protocol !== "https:") return

			if (
				url.pathname === window.location.pathname &&
				url.search === window.location.search &&
				url.hash !== ""
			) {
				return
			}

			event.preventDefault()
			navigateRef.current(url.pathname + url.search + url.hash)
		}

		document.body.addEventListener("click", handler)
		return () => document.body.removeEventListener("click", handler)
	}, [])

	useEffect(() => {
		const handler = () => setPath(window.location.pathname)
		window.addEventListener("popstate", handler)
		return () => window.removeEventListener("popstate", handler)
	}, [])

	// Keep the URL and auth state in sync: bounce signed-out visitors to
	// /login, and signed-in visitors away from the auth pages. The pages listed
	// in publicPage read fine either way: "/" describes the game, and the rest
	// are linked from emails and footers, so bouncing a reader to /login would
	// hide them from the people most likely to want them. Every one of them
	// must stay free of session-backed API calls: those endpoints 401 signed
	// out, and the fetch wrapper turns a 401 into a redirect to /login.
	// The account pages below are the only ones that call anything, and they
	// call auth endpoints — those go through raw fetch and never redirect.
	const authPage = path === "/login" || path === "/signup"
	// The static pages, separate from the rest of publicPage because they
	// also have to render for a signed-in *unverified* account: the
	// verification notice links to them in its footer, and gating them would
	// make those links do nothing.
	const infoPage = path === "/about" || path === "/privacy" || path === "/terms"
	// The account pages an email leads to, plus the form that asks for one of
	// those emails. All three render signed out — the whole point of /forgot
	// is that the visitor cannot log in, and /verify and /reset are opened on
	// whichever device read the mail. They render for a signed-in visitor too
	// (see below), so they are handled ahead of the auth gate rather than
	// only in the signed-out branch.
	const accountPage =
		path === "/verify" || path === "/forgot" || path === "/reset"
	const publicPage = path === "/" || infoPage || accountPage
	// A session in a kid profile is in kid mode: the shell, the navbar and the
	// route set all change. It is per session, so this is read off /api/auth/me
	// and nowhere else.
	const kidMode = user !== "loading" && user !== null && user.profile !== null
	useEffect(() => {
		if (user === "loading") return
		if (user === null && !authPage && !publicPage) {
			navigate("/login")
		} else if (user !== null && authPage) {
			navigate("/")
		} else if (kidMode && kidBlockedPath(path)) {
			// Bounce here rather than rendering KidHome under /friends: the URL
			// and what is on screen must not disagree.
			navigate("/")
		}
	}, [user, authPage, publicPage, kidMode, path, navigate])

	if (user === "loading") {
		if (sessionError) {
			return (
				<div class="h-100 flex center jc-center p-3">
					<div class="stack gap-3" style="max-width: 24rem;">
						<div class="alert danger">{sessionError}</div>
						<div>
							<button type="button" class="primary" onClick={loadSession}>
								Try again
							</button>
						</div>
					</div>
				</div>
			)
		}
		return (
			<div class="h-100 flex center jc-center">
				<div class="spinner" role="status" />
			</div>
		)
	}

	if (authPage) {
		const mode = path === "/signup" ? "signup" : "login"
		// Keyed by mode so switching pages resets form state and errors.
		// login/signup only return {id, username}, so re-fetch /me to get the
		// full profile (email, verification state) into app state.
		return <Login key={mode} mode={mode} onAuthenticated={loadSession} />
	}

	// These stay public even for a signed-in visitor: the link may well be for
	// the second account in the family, opened in the same browser, and a
	// signed-in session is no reason to hide the page that resets a password
	// nobody can remember.
	if (accountPage) {
		return <div class="h-100">{renderPublicRoute(path)}</div>
	}

	// An unverified account has nothing to render: the API refuses it
	// everywhere except the verification endpoints, so the notice *is* the
	// app until the link is clicked.
	if (user !== null && !user.email_verified) {
		if (infoPage) {
			return <div class="h-100">{renderPublicRoute(path)}</div>
		}
		return (
			<VerifyNotice
				email={user.email}
				onLoggedOut={() => setUser(null)}
				// Resolves to an error message, or "" when the check came back.
				// A failed check must not clear `user`: that would log an
				// unverified-but-valid session out on a flaky connection.
				onRecheck={() =>
					fetchMe()
						.then((me) => {
							setUser(me)
							return ""
						})
						.catch((e: unknown) =>
							e instanceof Error ? e.message : "Couldn't check your account.",
						)
				}
			/>
		)
	}

	if (user === null) {
		if (publicPage) {
			// Signed out there is no navbar to render around these; each page
			// carries its own links (SiteFooter).
			return <div class="h-100">{renderPublicRoute(path)}</div>
		}
		// The effect above is about to navigate to /login; render nothing for
		// this one frame rather than a flash of protected content.
		return null
	}

	// Playing is full-screen: the navbar would only steal vertical space and
	// tempt a mis-tap mid-question. Those views carry their own way back home.
	const playing =
		parseSoloRoute(path) !== null || parseMatchRoute(path) !== null

	// Kid mode: two routes and a navbar of its own. A solo round is the same
	// component and the same full-screen treatment as for the owner.
	if (user.profile !== null) {
		if (kidBlockedPath(path)) {
			// The effect above is navigating to "/" — render nothing for this one
			// frame rather than a flash of a page this profile may not have.
			return null
		}
		const soloId = parseSoloRoute(path)
		return (
			<div class="h-100 stack">
				{soloId === null && (
					<KidNavbar
						me={user}
						profile={user.profile}
						onProfileChanged={onProfileChanged}
					/>
				)}
				<div class="grow overflow-auto">
					{soloId !== null ? (
						<SoloPlay key={soloId} id={soloId} navigate={navigate} />
					) : (
						<KidHome
							key={user.profile.id}
							profile={user.profile}
							navigate={navigate}
						/>
					)}
				</div>
			</div>
		)
	}

	return (
		<div class="h-100 stack">
			{!playing && (
				<Navbar path={path} user={user} onLoggedOut={() => setUser(null)} />
			)}
			<div class="grow overflow-auto">
				{renderRoute(path, navigate, user, onProfileChanged)}
			</div>
		</div>
	)
}

// renderPublicRoute covers the routes that render signed out. Signed-in users
// share every one of them except "/", which is the game itself for them.
function renderPublicRoute(path: string) {
	if (path === "/about") return <About />
	if (path === "/privacy") return <Legal kind="privacy" />
	if (path === "/terms") return <Legal kind="terms" />
	if (path === "/verify") return <VerifyEmail />
	if (path === "/forgot") return <ForgotPassword />
	if (path === "/reset") return <ResetPassword />
	return <Landing />
}

function renderRoute(
	path: string,
	navigate: (path: string) => void,
	user: Me,
	onProfileChanged: () => void,
) {
	if (path === "/friends") {
		return <FriendsView navigate={navigate} />
	}

	if (path === "/about" || path === "/privacy" || path === "/terms") {
		return renderPublicRoute(path)
	}

	if (path === "/me") {
		return (
			<Profile me={user} username={null} onProfileChanged={onProfileChanged} />
		)
	}

	const profileUsername = parseUserRoute(path)
	if (profileUsername !== null) {
		return (
			<Profile
				key={profileUsername}
				me={user}
				username={profileUsername}
				onProfileChanged={onProfileChanged}
			/>
		)
	}

	const soloId = parseSoloRoute(path)
	if (soloId !== null) {
		return <SoloPlay key={soloId} id={soloId} navigate={navigate} />
	}

	const matchId = parseMatchRoute(path)
	if (matchId !== null) {
		return <MatchPlay key={matchId} id={matchId} me={user} />
	}

	// "/" and any unrecognized path both land on Home.
	return (
		<Home me={user} navigate={navigate} onProfileChanged={onProfileChanged} />
	)
}
